import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Grid, Typography, Box, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import tw from "twin.macro";

// Japanese-inspired container with appropriate spacing
const Container = tw.div`relative mx-auto px-0 py-0 max-w-screen-xl`;

// Clean container with full outline border
const MessageContainer = styled(Paper)(({ theme }) => ({
  width: "100%",
  position: "relative",
  margin: "0 auto",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
  borderRadius: 4,
  overflow: "hidden",
  display: "flex",
  flexDirection: "row",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const MessageContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  display: "flex",
  flexDirection: "column",
  position: "relative",
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(2.5, 3.5),
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2.5),
  },
}));

const ImageColumn = styled(Box)(({ theme }) => ({
  position: "relative",
  borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    borderLeft: "none",
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
  },
}));

const DateIndicator = styled(Typography)(({ theme }) => ({
  fontFamily: "'Noto Sans JP', sans-serif",
  fontSize: "0.8rem",
  color: "#666",
  marginBottom: theme.spacing(2),
  display: "inline-flex",
  alignItems: "center",
  position: "relative",
  paddingLeft: theme.spacing(2.5),
  letterSpacing: "0.03em",
  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    width: 12,
    height: 1,
    backgroundColor: "#0c4d96",
  },
}));

const DirectorNameContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  borderTop: "1px solid rgba(0, 0, 0, 0.08)",
}));

const MessageHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Noto Sans JP', sans-serif",
  fontWeight: 500,
  color: "#333",
  marginBottom: theme.spacing(2),
  letterSpacing: "0.02em",
  position: "relative",
  paddingBottom: theme.spacing(0.75),
  "&:after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: 0,
    width: 40,
    height: 2,
    backgroundColor: "#0c4d96",
  },
}));

// const SealMark = styled(Box)(({ theme }) => ({
//   width: 40,
//   height: 40,
//   borderRadius: "50%",
//   border: "1.5px solid rgba(12, 77, 150, 0.6)",
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
//   marginLeft: theme.spacing(1.5),
//   position: "relative",
//   "&:before": {
//     content: '""',
//     position: "absolute",
//     top: "50%",
//     left: "50%",
//     transform: "translate(-50%, -50%)",
//     width: 24,
//     height: 24,
//     borderRadius: "50%",
//     backgroundColor: "rgba(12, 77, 150, 0.1)",
//   },
// }));

// Main component
const DirectorMessage = ({
  imageSrc = "/images/director.jpg",
  messageDate = "March 20, 2025",
  directorName = "Taro Yamada",
  directorTitle = "代表取締役社長 | Chief Executive Officer",
}) => {
  const { t } = useTranslation();

  // Simple animation
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <Container>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeIn}
      >
        <MessageContainer elevation={0}>
          <Grid container>
            {/* Left side - Message content */}
            <Grid item xs={12} md={8} display="flex" alignItems="center">
              <MessageContent>
                <DateIndicator>{messageDate}</DateIndicator>

                <MessageHeading variant="h5">
                  {t("director_message.heading", "代表メッセージ")}
                </MessageHeading>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: "0.9rem",
                    lineHeight: 1.7,
                    color: "#333",
                    letterSpacing: "0.01em",
                  }}
                >
                  日本クラウド株式会社（NCC）は、急速に進化するテクノロジーの世界で、企業の成長を支え、新たな可能性を切り拓くことを使命とし、
                  AIやクラウドにとどまらず、時代の変化を的確に捉え、最適なソリューションを提供することを追求し続けてきました。{" "}
                  <br /> <br />
                  NCCは既存の枠組みにとらわれることなく、新たな技術がもたらす価値をいち早く見極め、柔軟に取り入れ、
                  未来を見据え、企業がより強く、持続可能な成長を遂げるための支援を続けています。{" "}
                  <br /> <br />
                  現在はクラウドインフラの最適化や業務効率化を目的としたソリューションの開発を進めるとともに、
                  最新技術を活用した情報管理や意思決定の支援にも注力し、企業の課題解決に取り組んでいます。
                  こうした取り組みを通じて、企業の可能性を広げ、より強くしなやかなビジネス環境の実現を目指します。{" "}
                  <br /> <br />
                  これからも日本国内外の企業とともに成長し、未来を見据えたITソリューションを提供し続けます。
                  <br />
                  今後とも日本クラウド株式会社 をよろしくお願いいたします。
                </Typography>
              </MessageContent>
            </Grid>

            {/* Right side - Image and info */}
            <Grid item xs={12} md={4}>
              <ImageColumn>
                <Box
                  sx={{
                    height: { xs: 280, sm: 320, md: "70%" },
                    position: "relative",
                  }}
                >
                  <img
                    src={imageSrc}
                    alt={directorName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center 20%",
                    }}
                  />
                </Box>

                <DirectorNameContainer>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "'Noto Sans JP', sans-serif",
                          fontSize: "1rem",
                          fontWeight: 500,
                          color: "#333",
                          textAlign: "right",
                          mb: 0.5,
                        }}
                      >
                        {directorName}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "'Noto Sans JP', sans-serif",
                          fontSize: "0.75rem",
                          color: "#666",
                          textAlign: "right",
                        }}
                      >
                        {directorTitle}
                      </Typography>
                    </Box>
                  </Box>
                </DirectorNameContainer>
              </ImageColumn>
            </Grid>
          </Grid>
        </MessageContainer>
      </motion.div>
    </Container>
  );
};

export default DirectorMessage;
